<!-- SectCompeModal.vue 宗门大比赛事页：海选 / 瑞士轮 / 淘汰赛对阵图，点任意一场弹出该场战报 -->
<template>
  <div class="sect-compe-container" :class="{ 'dark': isDark }">
    <div v-if="loading && !overview" class="compe-loading">
      <Spin size="large">加载赛事数据...</Spin>
    </div>

    <div v-else-if="!overview" class="compe-empty">
      <Icon type="ios-trophy-outline" size="48" />
      <p>暂无宗门大比记录，等到周四开赛后再来吧。</p>
    </div>

    <div v-else class="compe-body">
      <!-- 赛事总览 -->
      <div class="compe-header">
        <div class="compe-title">
          <span class="season">第 {{ overview.seasonNo }} 届宗门大比</span>
          <Tag :color="statusColor">{{ overview.statusName }}</Tag>
          <Button size="small" icon="ios-refresh" :loading="loading" @click="loadAll">刷新</Button>
        </div>
        <div class="compe-meta">
          <span>报名 <strong>{{ overview.enrollCount || 0 }}</strong> 人</span>
          <span v-if="overview.qualifierCount">海选 <strong>{{ overview.qualifierCount }}</strong> 人</span>
          <span v-if="overview.swissRoundTotal">瑞士轮 <strong>{{ overview.swissRoundTotal }}</strong> 轮</span>
          <span>开赛 {{ formatTime(overview.startTime) }}</span>
        </div>
        <div v-if="overview.championName" class="compe-podium">
          <div class="podium-item first">
            <span class="podium-label">首席</span>
            <span class="podium-name">{{ overview.championName }}</span>
            <span class="podium-sect">{{ overview.championSectName }}</span>
          </div>
          <div v-if="overview.runnerUpName" class="podium-item second">
            <span class="podium-label">次席</span>
            <span class="podium-name">{{ overview.runnerUpName }}</span>
            <span class="podium-sect">{{ overview.runnerUpSectName }}</span>
          </div>
          <div v-if="overview.thirdName" class="podium-item third">
            <span class="podium-label">三席</span>
            <span class="podium-name">{{ overview.thirdName }}</span>
            <span class="podium-sect">{{ overview.thirdSectName }}</span>
          </div>
        </div>
      </div>

      <Tabs v-model="activeTab" @on-click="onTabChange">
        <!-- 海选 -->
        <TabPane v-if="overview.hasQualifier" label="海选" name="qualifier">
          <div class="stage-tip">报名超过 32 人时开启海选，每人 2 场，按「胜场 → 战力 → 贡献」取前 32 进瑞士轮。</div>
          <div v-if="!qualifierRounds.length" class="stage-empty">海选还没开始。</div>
          <div v-for="r in qualifierRounds" :key="'q' + r.round" class="round-block">
            <div class="round-title">{{ r.roundName }}<small>共 {{ r.matchList.length }} 场</small></div>
            <div class="match-grid">
              <div
                v-for="m in r.matchList"
                :key="m.matchId"
                class="match-card"
                :class="matchClass(m)"
                @click="openRecord(m)"
              >
                <div class="match-seq">第 {{ m.seq }} 场</div>
                <div class="match-side" :class="{ 'win': isWinner(m, m.playerACharaId) }">
                  <span class="side-name">{{ m.playerAName }}</span>
                  <span class="side-sect">{{ m.playerASectName }}</span>
                </div>
                <div class="match-vs">{{ m.isBye === 1 ? '轮空' : 'VS' }}</div>
                <div v-if="m.isBye !== 1" class="match-side" :class="{ 'win': isWinner(m, m.playerBCharaId) }">
                  <span class="side-name">{{ m.playerBName }}</span>
                  <span class="side-sect">{{ m.playerBSectName }}</span>
                </div>
                <div v-else class="match-side empty"><span class="side-name">—</span></div>
              </div>
            </div>
          </div>
        </TabPane>

        <!-- 瑞士轮 -->
        <TabPane v-if="overview.hasSwiss" label="瑞士轮" name="swiss">
          <div class="stage-tip">首轮随机，之后同分相遇且尽量避开重复对手。胜一场 +1，最终按「积分 → Buchholz → 贡献 → 战力」取前 8。</div>
          <div class="standing-bar">
            <span>积分榜</span>
            <Select v-model="standingRound" size="small" style="width:150px" @on-change="loadStanding">
              <Option :value="0">出线名单</Option>
              <Option v-for="r in swissRounds" :key="'sr' + r.round" :value="r.round">{{ r.roundName }}后</Option>
            </Select>
          </div>
          <Table :columns="standingColumns" :data="standingList" size="small" :height="260" />

          <div v-for="r in swissRounds" :key="'s' + r.round" class="round-block">
            <div class="round-title">{{ r.roundName }}<small>共 {{ r.matchList.length }} 场</small></div>
            <div class="match-grid">
              <div
                v-for="m in r.matchList"
                :key="m.matchId"
                class="match-card"
                :class="matchClass(m)"
                @click="openRecord(m)"
              >
                <div class="match-seq">第 {{ m.seq }} 场</div>
                <div class="match-side" :class="{ 'win': isWinner(m, m.playerACharaId) }">
                  <span class="side-name">{{ m.playerAName }}</span>
                  <span class="side-sect">{{ m.playerASectName }}</span>
                </div>
                <div class="match-vs">{{ m.isBye === 1 ? '轮空' : 'VS' }}</div>
                <div v-if="m.isBye !== 1" class="match-side" :class="{ 'win': isWinner(m, m.playerBCharaId) }">
                  <span class="side-name">{{ m.playerBName }}</span>
                  <span class="side-sect">{{ m.playerBSectName }}</span>
                </div>
                <div v-else class="match-side empty"><span class="side-name">—</span></div>
              </div>
            </div>
          </div>
        </TabPane>

        <!-- 淘汰赛 -->
        <TabPane v-if="overview.hasKnockout" label="淘汰赛" name="knockout">
          <div class="stage-tip">瑞士轮前八单败淘汰，另设季军战。点任意一场看战报。</div>
          <div class="bracket">
            <div v-for="r in mainKnockoutRounds" :key="'k' + r.round" class="bracket-column">
              <div class="bracket-column-title">{{ r.roundName }}</div>
              <div
                v-for="m in r.matchList"
                :key="m.matchId"
                class="bracket-match"
                :class="matchClass(m)"
                @click="openRecord(m)"
              >
                <div class="bracket-side" :class="{ 'win': isWinner(m, m.playerACharaId) }">
                  <span class="side-name">{{ m.playerAName }}</span>
                  <span class="side-sect">{{ m.playerASectName }}</span>
                </div>
                <div class="bracket-side" :class="{ 'win': isWinner(m, m.playerBCharaId) }">
                  <span class="side-name">{{ m.isBye === 1 ? '轮空' : m.playerBName }}</span>
                  <span class="side-sect">{{ m.isBye === 1 ? '直接晋级' : m.playerBSectName }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="thirdPlaceRound" class="third-place">
            <div class="round-title">{{ thirdPlaceRound.roundName }}</div>
            <div class="match-grid">
              <div
                v-for="m in thirdPlaceRound.matchList"
                :key="m.matchId"
                class="match-card"
                :class="matchClass(m)"
                @click="openRecord(m)"
              >
                <div class="match-seq">争夺三席</div>
                <div class="match-side" :class="{ 'win': isWinner(m, m.playerACharaId) }">
                  <span class="side-name">{{ m.playerAName }}</span>
                  <span class="side-sect">{{ m.playerASectName }}</span>
                </div>
                <div class="match-vs">VS</div>
                <div class="match-side" :class="{ 'win': isWinner(m, m.playerBCharaId) }">
                  <span class="side-name">{{ m.playerBName }}</span>
                  <span class="side-sect">{{ m.playerBSectName }}</span>
                </div>
              </div>
            </div>
          </div>
        </TabPane>

        <!-- 我的战绩 -->
        <TabPane label="我的战绩" name="mine">
          <div v-if="!myHistory.length" class="stage-empty">本届你还没有对局记录。</div>
          <div v-else class="match-grid">
            <div
              v-for="m in myHistory"
              :key="'my' + m.matchId"
              class="match-card"
              :class="matchClass(m)"
              @click="openRecord(m)"
            >
              <div class="match-seq">{{ stageName(m.stage) }} · {{ roundLabel(m) }}</div>
              <div class="match-side" :class="{ 'win': isWinner(m, m.playerACharaId) }">
                <span class="side-name">{{ m.playerAName }}</span>
                <span class="side-sect">{{ m.playerASectName }}</span>
              </div>
              <div class="match-vs">{{ m.isBye === 1 ? '轮空' : 'VS' }}</div>
              <div v-if="m.isBye !== 1" class="match-side" :class="{ 'win': isWinner(m, m.playerBCharaId) }">
                <span class="side-name">{{ m.playerBName }}</span>
                <span class="side-sect">{{ m.playerBSectName }}</span>
              </div>
              <div v-else class="match-side empty"><span class="side-name">—</span></div>
            </div>
          </div>
        </TabPane>
      </Tabs>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SectCompeModal',
  props: {
    isDark: { type: Boolean, default: false },
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      overview: null,
      activeTab: 'knockout',
      qualifierRounds: [],
      swissRounds: [],
      knockoutRounds: [],
      standingList: [],
      standingRound: 0,
      myHistory: [],
      pollTimer: null,
      standingColumns: [
        { title: '名次', key: 'rankNo', width: 70, align: 'center' },
        { title: '弟子', key: 'charaName', minWidth: 110 },
        { title: '宗门', key: 'sectName', minWidth: 100 },
        { title: '积分', key: 'score', width: 70, align: 'center' },
        { title: 'Buchholz', key: 'buchholz', width: 90, align: 'center' },
        { title: '贡献', key: 'contribution', width: 90, align: 'center' },
        { title: '战力', key: 'capacity', width: 80, align: 'center' },
        { title: '轮空', key: 'byeCount', width: 70, align: 'center' }
      ]
    };
  },
  computed: {
    statusColor() {
      const s = this.overview ? this.overview.status : '';
      if (s === 'FINISHED') return 'gold';
      if (s === 'ABORTED') return 'default';
      if (s === 'READY') return 'blue';
      return 'success';
    },
    // 八强/四强/决赛画成对阵树，季军战单独放在下面
    mainKnockoutRounds() {
      return this.knockoutRounds.filter(r => r.round !== 4);
    },
    thirdPlaceRound() {
      return this.knockoutRounds.find(r => r.round === 4) || null;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadAll();
        this.startPoll();
      } else {
        this.stopPoll();
      }
    }
  },
  mounted() {
    if (this.visible) {
      this.loadAll();
      this.startPoll();
    }
  },
  beforeDestroy() {
    this.stopPoll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        const res = await this.$http.post('/gameSectCompe/overview');
        const data = res.data || {};
        if (data.status !== 200 || !data.data) {
          this.overview = null;
          return;
        }
        this.overview = data.data;
        // 默认落在最靠后的已开打阶段，赛事进行中时看到的就是最新战况
        if (this.overview.hasKnockout) {
          this.activeTab = 'knockout';
        } else if (this.overview.hasSwiss) {
          this.activeTab = 'swiss';
        } else if (this.overview.hasQualifier) {
          this.activeTab = 'qualifier';
        } else {
          this.activeTab = 'mine';
        }
        await Promise.all([
          this.overview.hasQualifier ? this.loadBracket('QUALIFIER') : Promise.resolve(),
          this.overview.hasSwiss ? this.loadBracket('SWISS') : Promise.resolve(),
          this.overview.hasKnockout ? this.loadBracket('KNOCKOUT') : Promise.resolve(),
          this.loadStanding(),
          this.loadMyHistory()
        ]);
      } catch (e) {
        console.error('加载宗门大比赛事失败', e);
      } finally {
        this.loading = false;
      }
    },
    async loadBracket(stage) {
      try {
        const res = await this.$http.post(
          '/gameSectCompe/bracket/?compeId=' + this.overview.compeId + '&stage=' + stage);
        const list = (res.data && res.data.data) || [];
        if (stage === 'QUALIFIER') this.qualifierRounds = list;
        else if (stage === 'SWISS') this.swissRounds = list;
        else this.knockoutRounds = list;
      } catch (e) {
        console.error('加载对阵图失败', stage, e);
      }
    },
    async loadStanding() {
      if (!this.overview) return;
      try {
        const res = await this.$http.post(
          '/gameSectCompe/standing/?compeId=' + this.overview.compeId + '&round=' + this.standingRound);
        this.standingList = (res.data && res.data.data) || [];
      } catch (e) {
        console.error('加载积分榜失败', e);
      }
    },
    async loadMyHistory() {
      const charaId = this.getCharaId();
      if (!charaId || !this.overview) return;
      try {
        const res = await this.$http.post(
          '/gameSectCompe/myHistory/?charaId=' + charaId + '&compeId=' + this.overview.compeId);
        this.myHistory = (res.data && res.data.data) || [];
      } catch (e) {
        console.error('加载我的战绩失败', e);
      }
    },
    getCharaId() {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; charaId=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return '';
    },
    onTabChange(name) {
      if (name === 'swiss') {
        this.loadStanding();
      }
    },
    // 赛事进行中才轮询，落幕后停掉，避免关掉弹窗还在打接口
    startPoll() {
      this.stopPoll();
      this.pollTimer = setInterval(() => {
        if (!this.visible) {
          this.stopPoll();
          return;
        }
        if (this.overview && !this.overview.running) {
          this.stopPoll();
          return;
        }
        this.loadAll();
      }, 15000);
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    openRecord(match) {
      if (match.isBye === 1) {
        this.$Message.info('轮空，无战报');
        return;
      }
      if (!match.recordId) {
        this.$Message.info('该场战报已过期');
        return;
      }
      this.$emit('duel2', match.recordId);
    },
    isWinner(match, charaId) {
      return !!charaId && match.winnerCharaId === charaId;
    },
    matchClass(match) {
      return { 'clickable': match.isBye !== 1 && !!match.recordId, 'is-bye': match.isBye === 1 };
    },
    stageName(stage) {
      if (stage === 'QUALIFIER') return '海选';
      if (stage === 'SWISS') return '瑞士轮';
      return '淘汰赛';
    },
    roundLabel(match) {
      if (match.stage !== 'KNOCKOUT') return '第 ' + match.round + ' 轮';
      const names = { 1: '八强', 2: '四强', 3: '决赛', 4: '季军战' };
      return names[match.round] || ('第 ' + match.round + ' 轮');
    },
    formatTime(time) {
      if (!time) return '—';
      const d = new Date(time);
      if (isNaN(d.getTime())) return time;
      const pad = n => (n < 10 ? '0' + n : '' + n);
      return (d.getMonth() + 1) + '月' + d.getDate() + '日 ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
    }
  }
};
</script>

<style scoped>
.sect-compe-container {
  padding: 8px 12px;
  color: #333;
}
.sect-compe-container.dark {
  color: #e6e6e6;
}
.compe-loading,
.compe-empty {
  text-align: center;
  padding: 60px 0;
  color: #999;
}
.compe-header {
  border-bottom: 1px solid rgba(150, 150, 150, 0.25);
  padding-bottom: 10px;
  margin-bottom: 8px;
}
.compe-title {
  display: flex;
  align-items: center;
  gap: 10px;
}
.compe-title .season {
  font-size: 18px;
  font-weight: 600;
}
.compe-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #888;
}
.compe-meta span {
  margin-right: 16px;
}
.compe-podium {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}
.podium-item {
  display: flex;
  flex-direction: column;
  min-width: 130px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid rgba(150, 150, 150, 0.3);
}
.podium-item.first {
  border-color: #f2c744;
  background: rgba(242, 199, 68, 0.12);
}
.podium-item.second {
  border-color: #b8c2cc;
  background: rgba(184, 194, 204, 0.12);
}
.podium-item.third {
  border-color: #cd8c52;
  background: rgba(205, 140, 82, 0.12);
}
.podium-label {
  font-size: 12px;
  color: #999;
}
.podium-name {
  font-size: 15px;
  font-weight: 600;
}
.podium-sect {
  font-size: 12px;
  color: #888;
}
.stage-tip {
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
  line-height: 1.6;
}
.stage-empty {
  padding: 30px 0;
  text-align: center;
  color: #999;
}
.standing-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
}
.round-block {
  margin-top: 12px;
}
.round-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
}
.round-title small {
  font-weight: 400;
  color: #999;
  margin-left: 8px;
}
.match-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.match-card {
  width: 210px;
  padding: 6px 10px;
  border: 1px solid rgba(150, 150, 150, 0.3);
  border-radius: 6px;
  transition: all 0.15s;
}
.match-card.clickable {
  cursor: pointer;
}
.match-card.clickable:hover {
  border-color: #2d8cf0;
  box-shadow: 0 0 6px rgba(45, 140, 240, 0.4);
}
.match-card.is-bye {
  opacity: 0.65;
}
.match-seq {
  font-size: 11px;
  color: #999;
  margin-bottom: 2px;
}
.match-side {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 1px 0;
}
.match-side.empty {
  color: #bbb;
}
.match-side .side-name {
  font-size: 13px;
}
.match-side .side-sect {
  font-size: 11px;
  color: #999;
}
.match-side.win .side-name {
  color: #19be6b;
  font-weight: 600;
}
.match-vs {
  font-size: 11px;
  color: #c5c8ce;
  text-align: center;
  line-height: 1;
}
.bracket {
  display: flex;
  gap: 24px;
  align-items: stretch;
  overflow-x: auto;
  padding-bottom: 8px;
}
.bracket-column {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  min-width: 190px;
  gap: 8px;
}
.bracket-column-title {
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  color: #888;
}
.bracket-match {
  border: 1px solid rgba(150, 150, 150, 0.3);
  border-radius: 6px;
  padding: 6px 10px;
  transition: all 0.15s;
}
.bracket-match.clickable {
  cursor: pointer;
}
.bracket-match.clickable:hover {
  border-color: #2d8cf0;
  box-shadow: 0 0 6px rgba(45, 140, 240, 0.4);
}
.bracket-match.is-bye {
  opacity: 0.65;
}
.bracket-side {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 2px 0;
}
.bracket-side .side-name {
  font-size: 13px;
}
.bracket-side .side-sect {
  font-size: 11px;
  color: #999;
}
.bracket-side.win .side-name {
  color: #19be6b;
  font-weight: 600;
}
.third-place {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px dashed rgba(150, 150, 150, 0.3);
}
</style>



// WEBPACK FOOTER //
// src/components/SectCompeModal.vue